import FormField from '@ingka/form-field';
import Select, { Option } from '@ingka/select';
import type { Control, FieldValues, Path } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { shouldValidate } from '../../utils';
import { FormFieldChildren } from '../controlled-year-month-dropdown/FormFieldChildren';

export type ControlledDropdownProps<TFormData extends FieldValues = FieldValues> = {
  id: string;
  control: Control<TFormData>;
  name: Path<TFormData>;
  label: string;
  errorMessage: string | undefined;
  disabled?: boolean;
  options: Array<{ name: string; value: string }>;
  hintText: string;
};

export const ControlledDropdown = <TFormData extends FieldValues = FieldValues>({
  id,
  control,
  name,
  label,
  disabled = false,
  errorMessage,
  options,
  hintText,
}: ControlledDropdownProps<TFormData>) => {
  const { t } = useTranslation();

  return (
    <Controller
      render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => {
        return (
          <FormField
            shouldValidate={shouldValidate(error)}
            valid={!errorMessage}
            validation={{
              msg: errorMessage ? `${t(errorMessage)}` : undefined,
              type: 'error',
            }}
          >
            <FormFieldChildren
              render={(describedById) => (
                <Select
                  id={id}
                  describedById={describedById}
                  label={label}
                  onChange={(e, isHintText) => {
                    if (isHintText) {
                      onChange({ target: { value: '' } });
                    } else {
                      onChange(e);
                    }
                  }}
                  onBlur={onBlur}
                  disabled={disabled}
                  hintText={hintText}
                  value={value === '' ? undefined : value}
                >
                  {options.map(({ name, value }) => (
                    <Option key={name} value={value} name={name} />
                  ))}
                </Select>
              )}
            />
          </FormField>
        );
      }}
      name={name}
      control={control}
    />
  );
};
